import { useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { useToast } from "@songtradr-one/components-frontend";
import { getErrorMessage } from "../../helpers/get-error-message";
import { ActionRunnerConfig, RunOptions } from "./useActionRunner.Parts";

export const useActionRunner = <TAction extends string = string>({
  refetchQueries,
  errorText,
}: ActionRunnerConfig) => {
  const client = useApolloClient();
  const { addToast } = useToast();
  const [running, setRunning] = useState<RunOptions<TAction> | null>(null);
  // Guards against a second action starting before the first re-rendered.
  const runningRef = useRef(false);

  const notify = (error: unknown) =>
    addToast({ text: getErrorMessage(error, errorText), variant: "attention" });

  // Not client.refetchQueries: that refetches the preloaded query refs
  // themselves, so a failed refetch rejects the ref and useReadQuery throws to
  // the route's error element. client.query only writes the cache on success.
  const refetch = () =>
    Promise.all(
      [...client.getObservableQueries(refetchQueries).values()].map(
        ({ query, variables }) =>
          client.query({ query, variables, fetchPolicy: "network-only" }),
      ),
    );

  /** Undefined if it failed or another action was running. */
  const run = async <TResult>(
    mutate: () => Promise<TResult>,
    options: RunOptions<TAction> = {},
  ): Promise<TResult | undefined> => {
    if (runningRef.current) return undefined;
    runningRef.current = true;
    setRunning(options);
    let result: TResult | undefined;
    try {
      result = await mutate();
      if (options.successText) {
        addToast({ text: options.successText, variant: "success" });
      }
    } catch (error) {
      notify(error);
    }
    try {
      await refetch();
    } catch (error) {
      notify(error);
    } finally {
      runningRef.current = false;
      setRunning(null);
    }
    return result;
  };

  return {
    busy: running !== null,
    running: running?.action ?? null,
    run,
  };
};
